import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { DefaultFormFields, FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { validateDateThatIsEqualOrAfter } from '@insurancemenu/imenu-components-lib/dist/helpers/validateDateOfBirth';
import {
  getCurrentDateInMDTFormat,
  getCurrentDateISO,
} from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import {
  applyProposalUserAction,
  updateInitiateFormAnswers,
  saveProposal as saveProposalToEnrollmentService,
} from 'api/operations/enrollmentService';
import { EnrollmentPeriod, Proposal, SaveProposalInput, USER_ACTIONS } from 'types/enrollmentService';
import { getCart, saveProposal as saveProposalToCartService } from 'api/operations/cartServiceV2';
import { Cart } from 'types/cartServiceV2';
import { Group, InputGroup } from 'types/groupService';
import { convertNewSaleCartIntoProductLine } from 'global/services-helpers/cartService';
import { getGroupById, updateGroup } from 'api/operations/groupService';
import { FetchResult } from 'apollo-link';
import { buildInputGroupFromGroup } from 'global/services-helpers/groupService';
import { INITIAL_GROUP_OBJECT } from 'store/domain/groupStore';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';

interface RFQInitiationFormFields extends DefaultFormFields {
  proposalName: FormControl<string>;
  responseDueDate: FormControl<string>;
  notes: FormControl<string>;
}

interface SavePeriodInput {
  proposalInput: SaveProposalInput;
  inputGroup: InputGroup;
}

export default class RFQInitiationPageStore {
  private _isLoading = false;
  private _initiateRFQForm: FormController<RFQInitiationFormFields> =
    RFQInitiationPageStore.initiateRfqFormController();

  private _proposal: Proposal | null = null;
  private _group: Group | null = null;
  private _cart: Cart | null = null;
  private _enrollmentPeriodModule = new EnrollmentPeriodModule();

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public setRequestDueDate(date: string): void {
    this._initiateRFQForm.fields.responseDueDate.value = date;
  }

  public get initiateRFQForm(): FormController<RFQInitiationFormFields> {
    return this._initiateRFQForm;
  }

  public get groupId(): string | null {
    return this._group?.id || null;
  }

  private get formAnswers(): Record<string, string> {
    return this.initiateRFQForm.getFormDataObject() as Record<string, string>;
  }

  private setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  // eslint-disable-next-line class-methods-use-this
  private static initiateRfqFormController(): FormController<RFQInitiationFormFields> {
    return new FormController({
      proposalName: new FormControl<string>('', { validations: ['printableCharacters'], trim: true }),
      responseDueDate: new FormControl<string>('', {
        optional: false,
        customValidations: [
          {
            // Validation ensures the selected date is equal to or later than the current day.
            check: (value: string): boolean => validateDateThatIsEqualOrAfter(value, getCurrentDateISO()),
            msg: 'The date cannot be in the past',
          },
        ],
      }),
      notes: new FormControl<string>('', { optional: true }),
    });
  }

  public async getInitialData(cartId: string, periodId?: string): Promise<void> {
    this.setIsLoading(true);

    const cart = await getCart(cartId);

    await runInAction(async () => {
      this._cart = cart;
      if (!cart) throw new Error(`cart with such id doesn't exist: ${cartId}`);

      const requests = periodId
        ? [getGroupById(cart.groupId), this._enrollmentPeriodModule.uploadEnrollmentPeriod(periodId)]
        : [getGroupById(cart.groupId)];

      const results = await Promise.all(requests);

      runInAction(() => {
        this._group = results[0] as Group;
        if (periodId) {
          this._proposal = this._enrollmentPeriodModule.getProposalById(cartId);
        }

        this.populateForm();
        this.setIsLoading(false);
      });
    });
  }

  public async onSaveProposal(): Promise<string> {
    if (this.initiateRFQForm.isFormValid() && this.groupId && this._cart) {
      this.setIsLoading(true);

      const { proposalInput, inputGroup } = this.collectDataToSaveProposal(this._cart);

      const requests = [
        saveProposalToCartService(this._cart.id, this.initiateRFQForm.fields.proposalName.value),
        saveProposalToEnrollmentService(proposalInput, this._cart.rateSegment),
        updateGroup(inputGroup),
      ];

      const results = await Promise.all(requests);
      return runInAction(() => {
        this.setIsLoading(false);
        const newPeriodId =
          (results[1] as FetchResult<{ saveProposal: EnrollmentPeriod }>)?.data?.saveProposal?.id || '';
        return newPeriodId;
      });
    }
    return '';
  }

  public async updateProposalAnswers(periodId: string): Promise<void> {
    this.setIsLoading(true);
    if (this._group && this._cart?.id) {
      const inputGroup = buildInputGroupFromGroup(this._group);
      const cartId = this._cart.id;

      try {
        await Promise.all([updateInitiateFormAnswers(periodId, cartId, this.formAnswers), updateGroup(inputGroup)]);

        await runInAction(async () => {
          if (periodId) {
            await applyProposalUserAction(periodId, cartId, USER_ACTIONS.COMPLETE_INITIATE_RFQ);
          }
          this.setIsLoading(false);
        });
      } catch (error) {
        this.setIsLoading(false);
      }
    }
  }

  // Helpers
  private populateForm(): void {
    if (this._proposal) {
      // Fill form with previously saved answers
      this._initiateRFQForm.populateFields({ ...this._proposal?.initiateFormAnswers });
    } else {
      // Populate default proposal name
      this._initiateRFQForm.populateFields({
        proposalName: `${this._group?.name || ''} ${getCurrentDateInMDTFormat()}`,
      });
    }
  }

  // Prepare Data for API calls
  private collectDataToSaveProposal(cart: Cart): SavePeriodInput {
    const bc = cart.lastUpdateBrowsingCriteria;
    const address = { zip: bc?.zipCode, state: bc?.state, county: bc?.county };
    const inputProductLines = convertNewSaleCartIntoProductLine(cart);
    const proposalName = this.initiateRFQForm.fields.proposalName.value;

    const inputGroup = buildInputGroupFromGroup(this._group || INITIAL_GROUP_OBJECT);
    inputGroup.contactInfo.primaryAddress = { ...inputGroup.contactInfo.primaryAddress, ...address };

    return {
      proposalInput: {
        groupId: cart.groupId,
        cartName: proposalName,
        cartId: cart.id,
        effectiveDate: bc.effectiveDate,
        productLines: inputProductLines,
        initiateFormAnswers: this.formAnswers,
      },
      inputGroup,
    };
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const PageStoreContext = createContext(new RFQInitiationPageStore());

export const useRFQInitiationPageStore = (): RFQInitiationPageStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
